import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { crossSection, displayLetters, project, STROKE } from './geometry';
import { money } from './format';
import { addToCart, fetchCart, fetchPrice } from './api';
import type { Bootstrap, CartLine, ConfigState, PriceResponse, Profile, Strings } from './types';

/** Builds the opening configuration for a profile from its own defaults. */
function initialConfig(
	profile: Profile,
	materialId: number,
	colorId: number,
	notes = ''
): ConfigState {
	return {
		profileSlug: profile.slug,
		segments: profile.definition.segments.map((segment) => segment.default),
		angles: profile.definition.bends.map((bend) => bend.default_angle),
		directions: profile.definition.bends.map((bend) => bend.direction),
		lengthMm: profile.definition.defaults?.length_mm ?? 2000,
		qty: profile.definition.defaults?.qty ?? 1,
		materialId,
		colorId,
		notes,
	};
}

/**
 * The cross-section, with its segments lettered and its bends numbered.
 *
 * The labels are the whole point. Before this, a control reading "Lankstymas 1"
 * had no counterpart on the drawing, so there was no way to tell which bend it
 * meant. Now every input row has a marker you can find on the shape.
 */
function Drawing({
	config,
	hex,
	label,
	developedWidthMm,
	hemFlags,
	hemOpen,
	heading,
	bendStyles,
	letters,
	t,
}: {
	config: ConfigState;
	hex: string;
	label: string;
	/**
	 * Which segments are folded edges, from the profile definition. Server-declared:
	 * whether an edge is folded is a property of the part, not a customer choice.
	 */
	hemFlags: readonly boolean[];
	/** One letter per segment, or null where the customer has no control. */
	letters: readonly (string | null)[];
	/** Presentation rotation from the profile definition. */
	heading: number;
	/**
	 * Per bend: the stored angle is a deflection rather than an interior. From the
	 * profile definition, server-declared like the hems — the sheets mix the two
	 * conventions per part and every stored number is the sheet's own.
	 */
	bendStyles: readonly boolean[];
	/** Which hems the shop leaves slightly unpressed — drawn ajar (round 4). */
	hemOpen: readonly boolean[];
	developedWidthMm: number | null;
	t: Strings;
}): React.JSX.Element {
	const section = useMemo(
		() =>
			crossSection(
				config.segments,
				config.angles,
				config.directions,
				hemFlags,
				heading,
				bendStyles,
				hemOpen
			),
		[config.segments, config.angles, config.directions, hemFlags, heading, bendStyles, hemOpen]
	);

	/*
	 * The projection owns its own viewBox, sized to the outline PLUS every label.
	 * Nothing clips, and no space goes on guessed padding.
	 *
	 * The letters carry their millimetre value: a cross-section is a tall narrow
	 * shape, so in a panel wide enough to matter it can only fill the height, and
	 * on a fabrication drawing what the spare width carries is the dimensions.
	 */
	const view = useMemo(
		() => project(section, { segmentValuesMm: config.segments, segmentLetters: letters }),
		[section, config.segments, letters]
	);

	return (
		<figure className="sd-stage">
			<svg
				className="sd-drawing"
				/*
				 * The part's own proportions, so the mobile rule can give it the
				 * height it needs instead of a fixed one. Presentation only.
				 */
				style={
					{
						'--sd-drawing-aspect': `${view.width} / ${view.height}`,
					} as React.CSSProperties
				}
				viewBox={view.viewBox}
				role="img"
				aria-label={label}
				preserveAspectRatio="xMidYMid meet"
			>
				<path
					d={view.path}
					fill="none"
					stroke={hex}
					strokeWidth={STROKE.main}
					vectorEffect="non-scaling-stroke"
					strokeLinejoin="round"
					strokeLinecap="round"
				/>
				{/*
				 * The folded edges, in the part's own colour and weight because they ARE
				 * the part. A closed hem lies against the face rather than continuing
				 * from it, so it cannot be part of the outline path.
				 */}
				{view.hemPath === '' ? null : (
					<path
						d={view.hemPath}
						fill="none"
						stroke={hex}
						strokeWidth={STROKE.main}
						vectorEffect="non-scaling-stroke"
						strokeLinejoin="round"
						strokeLinecap="round"
					/>
				)}
				<path
					d={view.path}
					fill="none"
					stroke="rgba(0,0,0,0.45)"
					strokeWidth={STROKE.hairline}
					vectorEffect="non-scaling-stroke"
					strokeLinejoin="round"
					strokeLinecap="round"
				/>
				{view.hemPath === '' ? null : (
					<path
						d={view.hemPath}
						fill="none"
						stroke="rgba(0,0,0,0.45)"
						strokeWidth={STROKE.hairline}
						vectorEffect="non-scaling-stroke"
						strokeLinejoin="round"
						strokeLinecap="round"
					/>
				)}
				{view.segmentLabels.map((annotation) => (
					<text
						key={`s-${annotation.text}`}
						className="sd-annotation sd-annotation-segment"
						x={annotation.x}
						y={annotation.y}
						textAnchor="middle"
						dominantBaseline="central"
					>
						{annotation.text}
					</text>
				))}
				{view.bendLabels.map((annotation) => (
					<g key={`b-${annotation.text}`}>
						<circle
							className="sd-annotation-disc"
							cx={annotation.x}
							cy={annotation.y}
							r={11}
						/>
						<text
							className="sd-annotation sd-annotation-bend"
							x={annotation.x}
							y={annotation.y}
							textAnchor="middle"
							dominantBaseline="central"
						>
							{annotation.text}
						</text>
					</g>
				))}
			</svg>
			<figcaption className="sd-stage-meta">
				{/* Reserved whether or not a figure is present, so the drawing never jumps. */}
				<span className="sd-stage-metric">
					{developedWidthMm === null
						? ' '
						: fill(t.developedWidth, String(developedWidthMm))}
				</span>
			</figcaption>
		</figure>
	);
}

/**
 * What is already in the basket, kept visible while the next part is configured.
 *
 * Roofers order several different parts at once. Without this the customer had no
 * evidence anything had been added and no reason to believe a second part was
 * expected.
 */
function BasketSummary({
	lines,
	cartUrl,
	t,
}: {
	lines: CartLine[];
	cartUrl: string;
	t: Strings;
}): React.JSX.Element | null {
	if (lines.length === 0) {
		return null;
	}

	const total = lines.reduce((sum, line) => sum + line.totalCents, 0);

	return (
		<section className="sd-basket" aria-label={t.basketLabel}>
			<h2 className="sd-basket-title">
				{t.basketTitle} <span className="sd-count">{lines.length}</span>
			</h2>
			<ul className="sd-basket-list">
				{lines.map((line) => (
					<li key={line.key} className="sd-basket-line">
						<span className="sd-basket-name">{line.name}</span>
						<span className="sd-basket-dims">{line.summary}</span>
						<span className="sd-basket-qty">
							{line.qty} {t.unitsShort}
						</span>
						<span className="sd-basket-total">{money(line.totalCents)}</span>
					</li>
				))}
			</ul>
			<div className="sd-basket-foot">
				<span>{t.grandTotal}</span>
				<strong>{money(total)}</strong>
			</div>
			<a className="sd-basket-link" href={cartUrl}>
				{t.goToCart} →
			</a>
		</section>
	);
}

/**
 * Substitutes the single %s in a translated string.
 *
 * Deliberately not a template literal at the call site: a translator has to be able
 * to move the placeholder within the sentence, and Lithuanian word order does not
 * follow English.
 */
function fill(template: string, value: string): string {
	return template.replace('%s', value);
}

/**
 * A validation message that occupies no space until there is one.
 *
 * The element stays in the DOM at every moment so aria-describedby always points at
 * something real; it is the GRID ROW that collapses. Reserving a line under every
 * field instead cost 20px per row permanently, which on a phone is the scarcest
 * space on the screen.
 */
function Slot({
	id,
	message,
	wide = false,
}: {
	id: string;
	message: string | null | undefined;
	wide?: boolean;
}): React.JSX.Element {
	const shown = typeof message === 'string' && message !== '';

	return (
		<p className={`sd-slot${wide ? ' sd-slot-wide' : ''}${shown ? ' is-shown' : ''}`} id={id}>
			<span>{shown ? message : ''}</span>
		</p>
	);
}

/*
 * Tell WooCommerce to refresh its own cart fragments.
 *
 * The configurator keeps its basket summary in React state, so IT updated after an
 * add while the theme's header counter did not — a customer who adds a part and sees
 * the counter unchanged adds it again. WooCommerce refreshes the mini-cart and header
 * through jQuery events on document.body, which a React app has no reason to know
 * about unless it says so.
 *
 * Guarded on jQuery existing, because these are jQuery events and a native
 * dispatchEvent does not reach jQuery handlers. If the theme does not use fragments
 * there is nothing listening and nothing happens, which is the correct outcome rather
 * than an error.
 */
function refreshThemeCart(): void {
	const jq = (
		window as unknown as { jQuery?: (target: unknown) => { trigger: (name: string) => void } }
	).jQuery;

	if (typeof jq !== 'function') {
		return;
	}

	try {
		jq(document.body).trigger('wc_fragment_refresh');
	} catch {
		// A theme with a broken fragment handler must not take the configurator with it.
	}
}

export function App({ boot }: { boot: Bootstrap }): React.JSX.Element {
	/* Every user-facing string, translated server-side and passed in the payload. */
	const t = boot.i18n;
	const { profiles, materials, colors, limits } = boot.catalog;

	const [profile, setProfile] = useState<Profile | null>(null);
	const [config, setConfig] = useState<ConfigState | null>(null);
	const [price, setPrice] = useState<PriceResponse | null>(null);
	const [busy, setBusy] = useState(false);
	const [adding, setAdding] = useState(false);
	const [added, setAdded] = useState('');
	const [lines, setLines] = useState<CartLine[]>([]);
	const [cartUrl, setCartUrl] = useState('/cart/');
	const abortRef = useRef<AbortController | null>(null);

	/*
	 * The fixed price bar overlaps the end of the form, so the page reserves its
	 * height as bottom padding. That reserve was a hardcoded 150px fallback and
	 * the custom property it falls back FROM was never written by anything -- the
	 * stylesheet comment claiming a runtime measurement described code that did
	 * not exist. The bar actually measures ~106px, so 44px of every page was dead
	 * reserve, and it pushed the sticky drawing's release point 44px earlier.
	 *
	 * Measured, not guessed: the bar grows when the breakdown or an error message
	 * appears, and a constant would be wrong again the moment it does.
	 */
	const priceBarObserver = useRef<ResizeObserver | null>(null);

	/*
	 * A callback ref, not useEffect with an empty dependency list. The bar does not
	 * exist on mount -- the picker renders first -- so an on-mount effect found a
	 * null ref, took its early return and never ran again. Caught by measuring the
	 * reserve in a real browser: it was still the 150px fallback.
	 */
	const priceBarRef = useCallback((bar: HTMLDivElement | null): void => {
		priceBarObserver.current?.disconnect();
		priceBarObserver.current = null;

		if (bar === null || typeof ResizeObserver === 'undefined') {
			return;
		}

		const root = bar.closest('.sd-root');

		if (!(root instanceof HTMLElement)) {
			return;
		}

		const observer = new ResizeObserver(() => {
			root.style.setProperty('--sd-pricebar-height', `${Math.ceil(bar.offsetHeight) + 16}px`);
		});

		observer.observe(bar);
		priceBarObserver.current = observer;
	}, []);

	/*
	 * The last material and colour chosen, carried into the next part. A roofer
	 * ordering six flashings for one roof orders them in one colour; asking again
	 * each time is a question with a known answer.
	 */
	const [carried, setCarried] = useState({
		materialId: materials[0]?.id ?? 0,
		colorId: colors[0]?.id ?? 0,
	});

	/*
	 * Only the colours the chosen material can actually be made in.
	 *
	 * The list used to be global, so an unpainted metallic was offered the whole RAL
	 * palette and the order went through. An unpainted material now carries exactly
	 * one row — its natural finish — and the grid below collapses to a single line of
	 * text rather than a one-cell swatch grid asking the customer to choose.
	 *
	 * A material with no in-stock link is offered everything, and the server appends
	 * every such material to every colour so this filter yields the same set. The two
	 * rules are deliberately the same sentence: if one said "this material has none"
	 * while the other said "the whole table is empty", a single unlinked material
	 * would be shown the full palette here and refused on every swatch there.
	 */
	const availableColors = useMemo(() => {
		/*
		 * `?? []` is not defensive padding. A cached bootstrap from before this field
		 * existed carries no material_ids, and `.includes` on undefined throws — which
		 * blanks the whole configurator rather than degrading it. Breeze holds page
		 * HTML for hours and the catalogue payload for minutes, so the old shape
		 * genuinely outlives the deploy.
		 */
		const forMaterial = colors.filter((candidate) =>
			(candidate.material_ids ?? []).includes(config?.materialId ?? 0)
		);

		return forMaterial.length > 0 ? forMaterial : colors;
	}, [colors, config?.materialId]);

	const color =
		availableColors.find((candidate) => candidate.id === config?.colorId) ?? availableColors[0];

	/*
	 * Changing material can strand the selected colour on a material that does not
	 * offer it. Snap to the first one that is available rather than leaving a
	 * selection the server would then reject at add-to-cart.
	 */
	useEffect(() => {
		if (config === null || color === undefined || color.id === config.colorId) {
			return;
		}

		update({ colorId: color.id });
	}, [color, config?.colorId, config?.materialId]);

	useEffect(() => {
		void fetchCart(boot).then((cart) => {
			setLines(cart.lines);
			setCartUrl(cart.cartUrl);
		});
	}, [boot]);

	const choose = useCallback(
		(next: Profile) => {
			setProfile(next);
			setConfig(initialConfig(next, carried.materialId, carried.colorId));
			setPrice(null);
			setAdded('');
		},
		[carried]
	);

	// Spec 7.4: 350 ms debounce, a quiet state, never a blocking spinner.
	useEffect(() => {
		if (!config || !profile) {
			return;
		}

		const timer = setTimeout(() => {
			abortRef.current?.abort();
			const controller = new AbortController();
			abortRef.current = controller;
			setBusy(true);

			fetchPrice(boot, config, profile.version, controller.signal)
				.then((result) => {
					setPrice(result);
					setBusy(false);
				})
				.catch(() => {
					/* superseded by a newer keystroke */
				});
		}, 350);

		return () => {
			clearTimeout(timer);
		};
	}, [boot, config, profile]);

	const update = (patch: Partial<ConfigState>): void => {
		setConfig((current) => (current ? { ...current, ...patch } : current));
		setAdded('');
	};

	const setAt = (key: 'segments' | 'angles', index: number, value: number): void => {
		setConfig((current) => {
			if (!current) {
				return current;
			}

			const next = [...current[key]];
			next[index] = value;

			return { ...current, [key]: next };
		});
		setAdded('');
	};

	const errorFor = (field: string): string | undefined =>
		price?.errors.find((error) => error.field === field)?.message;

	/*
	 * WCAG 3.3.1: a rejected field says so programmatically, not only in colour and
	 * adjacent text.
	 */
	const invalidProps = (field: string): Record<string, string> =>
		errorFor(field) === undefined
			? {}
			: { 'aria-invalid': 'true', 'aria-describedby': `sd-err-${field}` };

	const submit = (): void => {
		if (!config || !profile) {
			return;
		}

		setAdding(true);

		void addToCart(boot, config, profile.version).then((result) => {
			setAdding(false);

			if (!result.ok) {
				setAdded(result.message);
				return;
			}

			// Deliberately NOT a redirect to the cart. The customer is mid-order and
			// almost certainly has another part to configure.
			setCarried({ materialId: config.materialId, colorId: config.colorId });
			setAdded(result.message);

			void fetchCart(boot).then((cart) => {
				setLines(cart.lines);
				setCartUrl(cart.cartUrl);
				refreshThemeCart();
			});
		});
	};

	if (!profile || !config) {
		return (
			<div className="sd-root sd-picking">
				<header className="sd-intro">
					<h2 className="sd-title">{t.pickTitle}</h2>
					<p className="sd-lede">{t.pickLede}</p>
				</header>
				{profiles.length === 0 ? (
					/*
					 * A fresh install seeds its demo shapes INACTIVE, so this is the
					 * state the front page is in until the owner activates one. Without
					 * this it renders a heading inviting the customer to choose, over an
					 * empty grid — which reads as broken rather than as "not yet".
					 */
					<p className="sd-lede sd-empty">{t.pickEmpty}</p>
				) : (
					<div className="sd-profile-grid">
						{profiles.map((candidate) => {
							const preview = project(
								crossSection(
									candidate.definition.segments.map((segment) => segment.default),
									candidate.definition.bends.map((bend) => bend.default_angle),
									candidate.definition.bends.map((bend) => bend.direction),
									candidate.definition.segments.map(
										(segment) => segment.is_hem === true
									),
									candidate.definition.presentation_heading_deg ?? 0,
									candidate.definition.bends.map(
										(bend) => bend.angle_style === 'deflection'
									),
									candidate.definition.segments.map(
										(segment) => segment.hem_closure === 'open'
									)
								),
								{ longEdge: 96, annotate: false }
							);

							return (
								<button
									key={candidate.slug}
									type="button"
									className="sd-profile-card"
									onClick={() => {
										choose(candidate);
									}}
								>
									<svg
										className="sd-profile-thumb"
										viewBox={preview.viewBox}
										aria-hidden="true"
									>
										<path
											d={preview.path}
											fill="none"
											stroke="currentColor"
											strokeWidth={STROKE.thumb}
											vectorEffect="non-scaling-stroke"
											strokeLinejoin="round"
											strokeLinecap="round"
										/>
										{preview.hemPath === '' ? null : (
											<path
												d={preview.hemPath}
												fill="none"
												stroke="currentColor"
												strokeWidth={STROKE.thumb}
												vectorEffect="non-scaling-stroke"
												strokeLinejoin="round"
												strokeLinecap="round"
											/>
										)}
									</svg>
									<span className="sd-profile-name">{candidate.name_lt}</span>
									{candidate.is_demo ? (
										<span className="sd-demo-badge">{t.demoBadge}</span>
									) : null}
								</button>
							);
						})}
					</div>
				)}
				<BasketSummary lines={lines} cartUrl={cartUrl} t={t} />
			</div>
		);
	}

	/*
	 * ONE DERIVATION, HANDED TO BOTH. The drawing and the input controls used to letter
	 * independently — the drawing by geometric position, the controls from the stored
	 * `id` — and they agreed only because the shipped ids happened to run A, B, C in
	 * order. They are given the same array now, so a customer who says „B is wrong" means
	 * one segment on both, and on the cart line, order screen and email, which derive it
	 * by the same rule on the server.
	 */
	const segmentLetters = displayLetters(
		profile.definition.segments.map((segment) => segment.editable !== false)
	);

	const unitIncl = price?.valid && price.price ? price.price.unit_incl_cents : null;
	const lineIncl = price?.valid && price.price ? price.price.line_incl_cents : null;
	const lineExcl = price?.valid && price.price ? price.price.line_excl_cents : null;

	return (
		<div className="sd-root sd-configuring">
			{/*
			 * Navigation for the whole screen, so it spans both columns rather than
			 * sitting inside the form as if it belonged to the right-hand column.
			 */}
			<button
				type="button"
				className="sd-back"
				onClick={() => {
					setProfile(null);
				}}
			>
				← {t.backToProfiles}
			</button>

			<div className="sd-visual">
				<Drawing
					config={config}
					hex={color?.hex ?? '#8a8a8a'}
					label={fill(t.drawingLabel, profile.name_lt)}
					developedWidthMm={price?.geometry?.developed_width_mm ?? null}
					hemFlags={profile.definition.segments.map((segment) => segment.is_hem === true)}
					hemOpen={profile.definition.segments.map(
						(segment) => segment.hem_closure === 'open'
					)}
					heading={profile.definition.presentation_heading_deg ?? 0}
					bendStyles={profile.definition.bends.map(
						(bend) => bend.angle_style === 'deflection'
					)}
					letters={segmentLetters}
					t={t}
				/>
			</div>

			<div className="sd-inputs">
				<div className="sd-inputs-head">
					<h2 className="sd-title">{profile.name_lt}</h2>
				</div>

				{/*
				 * Segments and bends interleaved, in the order they occur along the
				 * part: A, bend 1, B, bend 2, C. Two separate fieldsets could not show
				 * that a bend sits between two specific faces.
				 */}
				<section className="sd-group">
					<h3 className="sd-group-title">{t.groupShape}</h3>
					<p className="sd-group-note">{t.shapeNote}</p>

					<div className="sd-form-rows">
						{profile.definition.segments.map((segment, index) => {
							/*
							 * NO CONTROL FOR SOMETHING THAT CANNOT BE CHOSEN. A hem is a
							 * property of the part: a kraigas has folded edges, a sienos
							 * kampas has folded edges, and the customer does not decide.
							 * Offering a disabled input would still say "this is a
							 * parameter of your order", which it is not — it belongs in
							 * the drawing and on the production sheet.
							 *
							 * The server refuses a changed value regardless; this only
							 * stops the interface implying there was a decision.
							 */
							if (segment.editable === false) {
								return null;
							}

							/*
							 * The bend that FOLLOWS this segment, if there is one. Reading
							 * it once keeps the JSX honest about the fact that the last
							 * segment has no bend after it.
							 */
							const nextBend = profile.definition.bends[index];

							/*
							 * And no control for a fold either. The trailing hem is the
							 * LAST segment, so skipping it does not skip the fold before
							 * it — that bend sits on the previous, editable segment and
							 * would have rendered its own „Lankstymas 4: 0°" control for
							 * something nobody can change.
							 */
							const bend = nextBend?.editable === false ? undefined : nextBend;

							return (
								<div key={segment.id} className="sd-row-pair">
									<label className="sd-row sd-row-segment">
										<span className="sd-marker">
											{segmentLetters[index] ?? segment.id}
										</span>
										<span className="sd-row-label">{segment.label_lt}</span>
										<input
											type="number"
											inputMode="numeric"
											step={5}
											min={segment.min}
											max={segment.max}
											value={config.segments[index] ?? 0}
											onChange={(event) => {
												setAt(
													'segments',
													index,
													Number(event.target.value)
												);
											}}
											{...invalidProps(`segments.${index}`)}
										/>
										<span className="sd-unit">mm</span>
									</label>
									<Slot
										id={`sd-err-segments.${index}`}
										message={errorFor(`segments.${index}`)}
									/>

									{bend === undefined ? null : (
										<>
											<label className="sd-row sd-row-bend">
												<span className="sd-marker sd-marker-bend">
													{index + 1}
												</span>
												<span className="sd-row-label">
													{t.bend}{' '}
													<span className="sd-row-hint">
														{(config.directions[index] ?? 1) === 1
															? t.bendUp
															: t.bendDown}
													</span>
												</span>
												<input
													type="number"
													inputMode="numeric"
													step={1}
													min={bend.min}
													max={bend.max}
													value={config.angles[index] ?? 90}
													onChange={(event) => {
														setAt(
															'angles',
															index,
															Number(event.target.value)
														);
													}}
													{...invalidProps(`angles.${index}`)}
												/>
												<span className="sd-unit">°</span>
											</label>
											<Slot
												id={`sd-err-angles.${index}`}
												message={errorFor(`angles.${index}`)}
											/>
										</>
									)}
								</div>
							);
						})}
					</div>
					<Slot id="sd-err-segments" message={errorFor('segments')} wide />
				</section>

				<section className="sd-group">
					<h3 className="sd-group-title">{t.groupLengthQty}</h3>
					<div className="sd-duo">
						<label className="sd-row">
							<span className="sd-row-label">{t.length}</span>
							<input
								type="number"
								inputMode="numeric"
								step={limits.length_step_mm}
								min={limits.min_length_mm}
								max={limits.max_length_mm}
								value={config.lengthMm}
								onChange={(event) => {
									update({ lengthMm: Number(event.target.value) });
								}}
								{...invalidProps('length_mm')}
							/>
							<span className="sd-unit">mm</span>
						</label>
						<label className="sd-row">
							<span className="sd-row-label">{t.quantity}</span>
							<input
								type="number"
								inputMode="numeric"
								step={1}
								min={1}
								max={limits.max_qty_per_line}
								value={config.qty}
								onChange={(event) => {
									update({ qty: Number(event.target.value) });
								}}
								{...invalidProps('qty')}
							/>
							<span className="sd-unit">{t.unitsShort}</span>
						</label>
					</div>
					<Slot
						id="sd-err-length_mm"
						message={errorFor('length_mm') ?? errorFor('qty')}
						wide
					/>
					<span className="sd-visually-hidden" id="sd-err-qty">
						{errorFor('qty') ?? ''}
					</span>
				</section>

				<section className="sd-group">
					<h3 className="sd-group-title">{t.groupMaterial}</h3>
					<label className="sd-row sd-row-wide">
						<span className="sd-row-label">{t.coating}</span>
						<select
							aria-label={t.material}
							value={config.materialId}
							onChange={(event) => {
								update({ materialId: Number(event.target.value) });
							}}
						>
							{materials.map((material) => (
								<option key={material.id} value={material.id}>
									{material.name_lt}
								</option>
							))}
						</select>
					</label>

					{availableColors.length > 1 ? (
						<>
							<div className="sd-swatches">
								{availableColors.map((candidate) => (
									<button
										key={candidate.id}
										type="button"
										className={`sd-swatch${candidate.id === config.colorId ? ' is-selected' : ''}`}
										onClick={() => {
											update({ colorId: candidate.id });
										}}
										aria-pressed={candidate.id === config.colorId}
									>
										<span
											className="sd-swatch-chip"
											style={{ background: candidate.hex }}
											aria-hidden="true"
										/>
										{/*
										 * No title attribute. The browser rendered it detached in
										 * the corner, and the colour NAME is not supplementary
										 * detail a customer should have to hover for — it is the
										 * thing they are choosing. Both code and name are on the
										 * control.
										 */}
										<span className="sd-swatch-text">
											<span className="sd-swatch-code">{candidate.code}</span>
											<span className="sd-swatch-name">
												{candidate.name_lt}
											</span>
										</span>
									</button>
								))}
							</div>
							<p className="sd-group-note">{boot.i18n['colorDisclaimer']}</p>
						</>
					) : (
						/*
						 * One colour is not a choice. A single-cell swatch grid asks the
						 * customer to pick from a set of one, which reads as a broken filter;
						 * the disclaimer about on-screen colour accuracy is also pointless
						 * for bare metal. State the finish and move on.
						 */
						<p className="sd-group-note sd-only-finish">{color?.name_lt ?? ''}</p>
					)}

					<label className="sd-row sd-row-wide sd-row-notes">
						<span className="sd-row-label">{t.notes}</span>
						<textarea
							aria-label={t.notes}
							rows={2}
							value={config.notes}
							onChange={(event) => {
								update({ notes: event.target.value });
							}}
							placeholder={t.notesPlaceholder}
						/>
					</label>
				</section>

				{/*
				 * The basket sits at the foot of the control column, on the same side as
				 * the add button. It was under the drawing on the left, so after adding
				 * a part the confirmation appeared nowhere near where the user clicked.
				 */}
				<BasketSummary lines={lines} cartUrl={cartUrl} t={t} />
			</div>

			<div className="sd-pricebar" role="status" aria-live="polite" ref={priceBarRef}>
				<div className="sd-price-block">
					<span className="sd-price-label">
						{config.qty} {t.unitsShort}
						{unitIncl === null ? '' : ` × ${money(unitIncl)}`}
					</span>
					<span className="sd-price-figure">
						{lineIncl === null ? (
							<span className="sd-price-pending">
								{busy
									? boot.i18n['calculating']
									: (price?.errors[0]?.message ?? '—')}
							</span>
						) : (
							money(lineIncl)
						)}
					</span>
					<span className="sd-price-vat">
						{lineExcl === null ? ' ' : `${t.vatSuffix} ${money(lineExcl)}`}
					</span>
				</div>

				<div className="sd-actions">
					<span className={`sd-added${added === '' ? '' : ' is-shown'}`}>{added}</span>
					{/*
					 * THE WAY FORWARD, in the bar that is always on screen.
					 *
					 * The basket summary already carried a cart link, but it lives at the
					 * foot of the control column: after adding a part the customer had to
					 * scroll to find out what to do next, and on a phone the sticky bar
					 * covered the only exit. A configurator that takes an order and then
					 * hides the checkout is a configurator that does not take orders.
					 *
					 * It carries the count rather than a fixed label, so it doubles as the
					 * confirmation: adding a second part changes 1 to 2 in place. It appears
					 * only once there is something to go to.
					 */}
					{lines.length > 0 && (
						<a className="sd-bar-cart" href={cartUrl}>
							{t.goToCart} <span className="sd-count">{lines.length}</span>
						</a>
					)}
					<button
						type="button"
						className="sd-add"
						disabled={adding || lineIncl === null}
						onClick={submit}
					>
						{boot.i18n['addToCart']}
					</button>
				</div>
			</div>
		</div>
	);
}
